/**
 * Zustand store for theme preferences.
 *
 * Uses an adapter pattern for platform-specific persistence and native theming.
 * Cross-window sync handled via BroadcastChannel.
 *
 * On desktop, Tauri injects window.__ANIMUS_INITIAL_THEME__ before the page renders,
 * allowing synchronous initialization without waiting for async store load.
 */

import { create } from "zustand";

import type { ThemeAdapter, ThemePreference, ThemeStore } from "./types";

const BROADCAST_CHANNEL_NAME = "animus-theme-sync";

let broadcastChannel: BroadcastChannel | null = null;
let adapter: ThemeAdapter | null = null;

const THEME_CACHE_KEY = "animus-theme-preference";

/**
 * Get the initial theme synchronously.
 *
 * Checks in order:
 * 1. Tauri's injected global (for desktop, though it may not be set yet)
 * 2. localStorage cache (synced whenever theme changes)
 * 3. Falls back to "system"
 */
function getInitialTheme(): ThemePreference {
  if (typeof window === "undefined") {
    return "system";
  }

  // Check Tauri injection first
  const injected = (window as { __ANIMUS_INITIAL_THEME__?: string })
    .__ANIMUS_INITIAL_THEME__;
  if (injected === "light" || injected === "dark" || injected === "system") {
    return injected;
  }

  // Check localStorage cache
  try {
    const cached = localStorage.getItem(THEME_CACHE_KEY);
    if (cached === "light" || cached === "dark" || cached === "system") {
      return cached;
    }
  } catch {
    // localStorage might be unavailable
  }

  return "system";
}

/**
 * Cache theme to localStorage for synchronous access on next load.
 */
function cacheTheme(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_CACHE_KEY, preference);
  } catch {
    // Ignore errors
  }
}

function getBroadcastChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") {
    return null;
  }
  if (!broadcastChannel) {
    broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
  }
  return broadcastChannel;
}

function mapPreferenceToNative(
  preference: ThemePreference
): "light" | "dark" | "auto" {
  return preference === "system" ? "auto" : preference;
}

export const useThemeStore = create<ThemeStore>((set) => ({
  preference: getInitialTheme(),

  setPreference: (preference: ThemePreference) => {
    set({ preference });

    // Cache for synchronous access on next load
    cacheTheme(preference);

    // Persist and sync native theme
    if (adapter) {
      adapter.savePreference(preference);
      adapter.setNativeTheme(mapPreferenceToNative(preference));
    }

    // Broadcast to other windows
    const channel = getBroadcastChannel();
    if (channel) {
      channel.postMessage({ type: "theme-change", preference });
    }
  },
}));

/**
 * Initialize the theme store with a platform adapter.
 *
 * Call this once at app startup in each window.
 * Non-blocking - the initial theme is already available via localStorage cache.
 */
export async function initializeThemeStore(
  platformAdapter: ThemeAdapter
): Promise<void> {
  adapter = platformAdapter;

  // Load persisted preference and sync with localStorage cache
  const stored = await platformAdapter.loadPreference();
  if (stored) {
    useThemeStore.setState({ preference: stored });
    cacheTheme(stored);
    await platformAdapter.setNativeTheme(mapPreferenceToNative(stored));
  }

  // Listen for cross-window sync
  const channel = getBroadcastChannel();
  if (channel) {
    channel.onmessage = (event) => {
      if (event.data?.type === "theme-change") {
        const preference = event.data.preference as ThemePreference;
        useThemeStore.setState({ preference });
        cacheTheme(preference);
        // Native theme already set by the originating window
      }
    };
  }
}
